/**
 * pageLayoutExecutor — `set_page_layout` : les marges, l'orientation et le
 * format de la page, sur TOUTES les sections du document.
 *
 * La page est un réglage de section. « Passe le document en paysage » vaut pour
 * tout le document : chaque section reçoit les réglages, et le reçu les juge sur
 * chacune. Régler une seule section n'est pas offert par le contrat.
 *
 * Ce que chaque section reçoit, et ce que la relecture établit, se décide dans
 * `miseEnPage.ts` (pur), dont l'EN-TÊTE porte les mesures de l'éditeur. Ici :
 * les temps de tout verbe du canvas — instantané d'avant, gardes de capacité,
 * écritures sous `muterSousFences`, instantané d'après, reçu relu.
 *
 * - **Écriture directe, quel que soit le régime de suivi** : un réglage de
 *   section n'est pas du contenu, il ne laisse aucune révision (mesuré ; sous
 *   suivi activé : `tests/ws-mocked/canvas-mise-en-page.spec.ts`).
 * - **Le juge est le fichier exporté** (`lecteurDeForme.ts`, `sections`) : un
 *   export par appel, après les écritures. Conforme, différent ou non lisible,
 *   comme une écriture de forme.
 * - **Un refus en cours de route ne défait rien, et le dit** : le reçu est
 *   `partial`, compte les écritures faites (`pageLayout.writes` : ce
 *   qu'`undo_changes` défait), nomme la section où l'éditeur a refusé, et
 *   relit le fichier : `pageLayout.settings` dit ce que les sections portent.
 *   Une exception de l'éditeur sur une écriture est rendue comme un refus.
 */

import { CANVAS_PAGE_TEXT_AREA_CM_MIN, type CanvasActionInput, type CanvasPageLayoutReceipt, type CanvasPageMarginSide, type CanvasPageOrientation } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  avecUnConstat,
  buildVerifiedReceipt,
  partialAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
} from './actionReceipt';
import { idsDeRevision, operationDuDocument } from './canvasSnapshot';
import { relireLeFichierDeLActe } from './ecritureDeForme';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { ecrituresDePage, jugerLaPage, zoneDeTexteTropPetite, type EcritureDePage, type SectionPubliee, type ZoneDeTexteTropPetite } from './miseEnPage';
import { adresseDeSection, entrees, isRecord, refusDeCapacite as refusDeCapaciteDeLEditeur, refusDuFournisseur, type AdresseDeSection } from './reponsesDeLEditeur';
import { snapshot, type CanvasActionContext } from './textActionExecutors';

export type SetPageLayoutInput = Extract<CanvasActionInput, { action: 'set_page_layout' }>;

const nombre = (valeur: unknown): number | null => (typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null);

/** Une section publiée par l'éditeur : son adresse, et sa page en pouces. Sans adresse lisible : `null`. */
function lireLaSection(entree: Record<string, unknown>): { adresse: AdresseDeSection; page: SectionPubliee } | null {
  const adresse = adresseDeSection(entree);
  if (adresse === null) return null;
  const page = isRecord(entree.pageSetup) ? entree.pageSetup : {};
  const marges = isRecord(entree.margins) ? entree.margins : {};
  const orientation: CanvasPageOrientation | null = page.orientation === 'portrait' || page.orientation === 'landscape' ? page.orientation : null;
  const cote = (nom: CanvasPageMarginSide): number | null => nombre(marges[nom]);
  return {
    adresse,
    page: {
      largeurPouces: nombre(page.width),
      hauteurPouces: nombre(page.height),
      orientation,
      margesPouces: { top: cote('top'), bottom: cote('bottom'), left: cote('left'), right: cote('right') },
    },
  };
}

/** Ce qu'une écriture de section règle, dit en français dans un refus. */
const NOM_DE_L_ECRITURE: Readonly<Record<EcritureDePage['operation'], string>> = { setPageMargins: 'les marges', setPageSetup: "l'orientation ou le format" };

/** Les réglages non tenus, dits au modèle : le champ du reçu, la valeur lue, quoi dire à l'avocat. */
function messageDesReglagesNonTenus(settings: CanvasPageLayoutReceipt['settings'], sections: number, causeDeNonLecture: string | undefined): string {
  const differents = settings.filter((reglage) => reglage.outcome === 'different').map((reglage) => `${reglage.setting} (lu : ${(reglage.read ?? []).join(', ')})`);
  const nonLus = settings.filter((reglage) => reglage.outcome === 'unreadable').map((reglage) => reglage.setting);
  return `set_page_layout a écrit la page des ${sections} section(s) du document, mais la relecture du fichier de l'acte ne confirme pas tout `
    + '(pageLayout.settings) : '
    + [
      ...(differents.length === 0 ? [] : [`différent de ce qui était demandé — ${differents.join(' ; ')}`]),
      ...(nonLus.length === 0 ? [] : [`non lisible dans le fichier — ${nonLus.join(', ')}${causeDeNonLecture === undefined ? '' : ` (cause rendue telle quelle : « ${causeDeNonLecture} »)`}`]),
    ].join(' ; ')
    + ". N'annonce comme fait que les réglages \"conforming\" ; pour les autres, dis à l'avocat ce qui est lu, ou qu'il doit le vérifier à l'écran. "
    + "Ne rejoue pas l'appel à l'identique.";
}

const NOM_DE_MARGE: Readonly<Record<CanvasPageMarginSide, string>> = { top: 'haute', bottom: 'basse', left: 'gauche', right: 'droite' };

/** Une page sans place pour le texte, dite au modèle : la section, la dimension, les mesures en centimètres, quoi réduire. */
function messageDeZoneDeTexteTropPetite(refus: ZoneDeTexteTropPetite, sections: number): string {
  const marges = Object.entries(refus.margesCm) as Array<[CanvasPageMarginSide, number]>;
  const noms = marges.map(([cote]) => NOM_DE_MARGE[cote]).join(' et ');
  return `set_page_layout a été refusé avant toute écriture : sur la section ${refus.section} (${sections} section(s) au document), il resterait `
    + `${refus.resteCm} cm de ${refus.dimension} pour le texte — page de ${refus.pageCm} cm de ${refus.dimension}, marges `
    + `${marges.map(([cote, cm]) => `${NOM_DE_MARGE[cote]} ${cm} cm`).join(' et ')} (demandées, ou en place) ; le minimum est de ${CANVAS_PAGE_TEXT_AREA_CM_MIN} cm. `
    + `Rien n'a été modifié. Réduis les marges ${noms} (marginsCm), ou demande une page plus ${refus.dimension === 'largeur' ? 'large' : 'haute'} (paperSize, orientation).`;
}

/**
 * Règle la page de toutes les sections du document, puis relit le fichier exporté.
 */
export async function executeSetPageLayout(ctx: CanvasActionContext, input: SetPageLayoutInput): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const refuserAvant = (code: string, message: string, nextAction: string, action: 'stop' | 'reinspect' | 'retry' = 'stop'): CanvasActionReceipt => abortedBeforeMutation({
    intent: input.action,
    snapshot: toCanvasSnapshotSummary(pre),
    code,
    message,
    nextAction,
    recovery: { action, reason: "Rien n'a été écrit : le document est intact." },
  });
  /** @param seRegle Le verbe accordé au sujet `pour` : « se règle » pour la page, « se règlent » pour les marges. */
  const refusDeCapacite = (surface: string, pour: string, seRegle: 'se règle' | 'se règlent'): CanvasActionReceipt => refusDeCapaciteDeLEditeur({
    intent: input.action,
    resume: pre,
    surface,
    pourQuoi: `pour régler ${pour}`,
    nextAction: `Dis à l'avocat que ${pour} de ce document ${seRegle} dans Word, après téléchargement.`,
    recovery: { action: 'stop', reason: "Rien n'a été écrit : le document est intact." },
  });

  const lister = operationDuDocument(ctx.doc, 'sections', 'list');
  if (lister === undefined) return refusDeCapacite('sections.list', 'la page', 'se règle');
  const operations: Readonly<Record<EcritureDePage['operation'], ReturnType<typeof operationDuDocument>>> = {
    setPageMargins: operationDuDocument(ctx.doc, 'sections', 'setPageMargins'),
    setPageSetup: operationDuDocument(ctx.doc, 'sections', 'setPageSetup'),
  };
  // Toutes les surfaces que la demande exige, AVANT la première écriture : des marges écrites puis une
  // orientation refusée faute de surface laisseraient l'avocat avec la moitié de ce qu'il a demandé.
  if (input.marginsCm !== undefined && operations.setPageMargins === undefined) return refusDeCapacite('sections.setPageMargins', 'les marges', 'se règlent');
  if ((input.orientation !== undefined || input.paperSize !== undefined) && operations.setPageSetup === undefined) {
    return refusDeCapacite('sections.setPageSetup', "l'orientation et le format", 'se règlent');
  }

  const publiees = entrees(await lister({}));
  const sections = publiees.map(lireLaSection);
  if (sections.length === 0 || sections.includes(null)) {
    return refuserAvant(
      'SECTIONS_UNREADABLE',
      `set_page_layout a été refusé avant toute écriture : l'éditeur publie ${publiees.length} section(s), dont `
      + `${sections.filter((section) => section === null).length} sans adresse lisible. La page se règle sur toutes les sections ou sur aucune. Rien n'a été modifié.`,
      "Relis le document par canvas_inspect, puis rejoue l'appel une fois ; s'il est refusé de même, dis à l'avocat de régler la page dans Word.",
      'reinspect',
    );
  }

  // Une page où le texte n'aurait plus de place : l'éditeur l'écrirait (mesuré). Refusée avant la première écriture.
  const sansPlace = zoneDeTexteTropPetite(input, sections.flatMap((section) => (section === null ? [] : [section.page])));
  if (sansPlace !== null) {
    return refuserAvant(
      'PAGE_LAYOUT_NO_TEXT_AREA',
      messageDeZoneDeTexteTropPetite(sansPlace, sections.length),
      "Rejoue l'appel avec des marges plus petites ou une page plus grande ; lis la page en place par canvas_inspect avec formatOf:{kind:\"all\"} (format.page).",
      'retry',
    );
  }

  // La révision à présenter : celle de l'inspection pour la première écriture, relue pour les suivantes —
  // chaque écriture de section avance la révision du document (mesuré).
  const attendueInitiale = await revisionCourante(ctx, ctx.expectedRevision ?? pre.revision);
  let ecrites = 0;
  /** Le refus qui a arrêté les écritures, et où. Une exception de l'éditeur en est un : elle ne doit coûter ni le compte ni le reçu. */
  let arret: { refus: { code: string; message: string }; ecritureRefusee: string } | undefined;
  ecritures: for (const [rang, section] of sections.entries()) {
    if (section === null) continue;
    for (const ecriture of ecrituresDePage(input, section.page)) {
      const ecritureRefusee = `${NOM_DE_L_ECRITURE[ecriture.operation]} de la section ${rang} (${sections.length} section(s) au document)`;
      const operation = operations[ecriture.operation];
      // Les gardes de capacité plus haut couvrent ce que la demande exige ; une écriture qu'elles n'auraient pas prévue s'arrête ici, dite.
      if (operation === undefined) {
        arret = { refus: { code: 'CAPABILITY_UNAVAILABLE', message: `editor.doc.sections.${ecriture.operation} is not exposed by the mounted editor` }, ecritureRefusee };
        break ecritures;
      }
      const options = { changeMode: 'direct' as const, expectedRevision: ecrites === 0 ? attendueInitiale : await revisionCourante(ctx, attendueInitiale) };
      const recu = await muterSousFences(ctx, `set_page_layout:sections.${ecriture.operation}`, options, async () => {
        try {
          return await operation({ target: section.adresse, ...ecriture.entree }, options);
        } catch (erreur) {
          return { success: false, failure: { code: 'EDITOR_THREW', message: erreur instanceof Error ? erreur.message : String(erreur) } };
        }
      });
      if (isRecord(recu) && recu.success === true) {
        ecrites += 1;
        continue;
      }
      arret = { refus: refusDuFournisseur(recu), ecritureRefusee };
      break ecritures;
    }
  }
  if (arret !== undefined && ecrites === 0) {
    return refuserAvant(
      arret.refus.code,
      `set_page_layout a été refusé avant toute écriture : l'éditeur a refusé ${arret.ecritureRefusee}. Refus rendu tel quel : « ${arret.refus.message} ». Rien n'a été modifié.`,
      "Corrige la valeur refusée et rejoue l'appel, ou dis à l'avocat que ce réglage n'a pas pu être posé.",
    );
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque(arret === undefined ? 'set_page_layout:receipt' : 'set_page_layout:refus');

  // La postcondition : le fichier exporté, relu une fois — aussi après un refus, pour dire ce qui est réellement en place.
  const relu = await relireLeFichierDeLActe(ctx.doc);
  const causeDeNonLecture = 'cause' in relu ? relu.cause : undefined;
  const pageLayout: CanvasPageLayoutReceipt = { sections: sections.length, writes: ecrites, settings: jugerLaPage(input, 'acte' in relu ? relu.acte.sections : null) };
  const tenus = pageLayout.settings.every((reglage) => reglage.outcome === 'conforming');
  if (arret !== undefined) {
    return {
      ...partialAfterMutation({
        intent: input.action,
        preSnapshot: toCanvasSnapshotSummary(pre),
        postSnapshot: toCanvasSnapshotSummary(post),
        resolvedTargets: [],
        code: arret.refus.code,
        message: `set_page_layout n'a pas abouti : l'éditeur a refusé ${arret.ecritureRefusee}. Refus rendu tel quel : « ${arret.refus.message} ». `
          + `Les ${ecrites} écriture(s) déjà faites RESTENT dans le document (pageLayout.writes) : la page n'est plus la même d'une section à l'autre. `
          + (causeDeNonLecture === undefined
            ? 'pageLayout.settings dit, relu dans le fichier, ce que les sections portent maintenant. '
            : `Le fichier de l'acte n'a pas pu être relu (cause rendue telle quelle : « ${causeDeNonLecture} ») : pageLayout.settings ne dit rien de ce que les sections portent. `)
          + `Ne rejoue pas l'appel entier. Pour revenir à la page d'avant : undo_changes avec steps = ${ecrites}. Dis à l'avocat ce qui a changé.`,
        verification: [],
        effects: [{ kind: 'set_page_layout', reviewability: 'direct_untracked', description: `Avant le refus de l'éditeur : ${ecrites} écriture(s) de page, sur les premières sections.`, nodeIds: [] }],
        recoveryReason: "Une partie des sections est déjà réglée : rejouer l'appel entier ne déferait rien.",
      }),
      pageLayout,
    };
  }

  const recu = buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: { success: true },
    revisionsAvant: idsDeRevision(pre.trackedChanges),
    revisionsApres: idsDeRevision(post.trackedChanges),
    mouvementAttendu: 'aucun',
    verification: [{
      name: 'sections_reglees',
      passed: true,
      message: `L'éditeur a accepté les ${ecrites} écriture(s) de page, sur ${sections.length} section(s) ; ce qui était déjà en place n'a pas été réécrit.`,
    }],
    effects: [{
      kind: 'set_page_layout',
      reviewability: 'direct_untracked',
      description: `Page réglée sur les ${sections.length} section(s) du document (${pageLayout.settings.map((reglage) => `${reglage.setting} : ${reglage.requested}`).join(', ')}), `
        + `en ${ecrites} écriture(s) directes — l'avocat n'aura aucune modification suivie à accepter. Pour annuler : undo_changes avec steps = ${ecrites}`
        + (ecrites === 0 ? ' — rien à annuler, tout était déjà en place.' : '.'),
      nodeIds: [],
    }],
  });
  const avecLaPage = avecUnConstat(recu, {
    name: 'page_relue',
    passed: tenus,
    message: tenus
      ? "Relu dans le fichier de l'acte : chaque réglage demandé est porté par toutes les sections (pageLayout.settings)."
      : messageDesReglagesNonTenus(pageLayout.settings, sections.length, causeDeNonLecture),
  }, tenus ? undefined : {
    code: pageLayout.settings.some((reglage) => reglage.outcome === 'different') ? 'PAGE_LAYOUT_DIFFERENT' : 'PAGE_LAYOUT_UNVERIFIED',
    message: messageDesReglagesNonTenus(pageLayout.settings, sections.length, causeDeNonLecture),
  });
  return { ...avecLaPage, pageLayout };
}
